/**
 * "When did the roster last update, and should anybody worry?"
 *
 * Everything in here is pure. `today` is an argument, the runs are an argument,
 * and nothing reads a clock or a database — because the whole value of this
 * feature is that it tells the truth on a Monday, and a function that cannot be
 * put on an arbitrary Monday cannot be shown to.
 *
 * WHY WEEKDAYS AND NOT DAYS
 *
 * The Metabase pull runs on weekdays. On a Monday morning the last successful
 * run is legitimately three calendar days old, so a naive "two days is a
 * problem" rule goes amber every single Monday. Renee learns within a fortnight
 * that the warning means nothing, and then it is worthless on the day it is
 * real. Counting working days is the difference between an alert and wallpaper.
 *
 * WHY THERE IS A CUTOFF DATE
 *
 * `sync_runs` holds a `daily_metabase / 2026-08-13 / ok` row written during
 * seeding, before any sync existed. Read naively it says the roster last
 * updated on 13 August, which is both false and reassuring — the worst pair.
 * Rows before `IGNORE_BEFORE` are not evidence of anything and are dropped.
 */

export type SyncResult = 'ok' | 'running' | 'failed' | 'aborted'

export type SyncRun = {
  id: number
  kind: string
  /** `YYYY-MM-DD`, computed server-side in America/New_York. */
  run_date: string
  result: SyncResult
  reason: string | null
  message: string | null
  stats: Record<string, unknown> | null
  started_at: string | null
  finished_at: string | null
}

/**
 * Runs dated before this are seeding artefacts, not evidence.
 *
 * A date rather than a row id, because the row this exists to exclude is
 * identified by its date and a re-seed would write a new id.
 */
export const IGNORE_BEFORE = '2026-08-18'

/**
 * How many recent runs anybody reads.
 *
 * Shared by the strip and the watchdog endpoint on purpose. They used 60 and
 * 120, under a comment claiming they read "the same two queries" — and the
 * whole argument for sharing `syncStatus` is that identical inputs are what
 * stop the screen and the email disagreeing. A shared function handed different
 * rows is two answers wearing one name.
 *
 * Enough to describe a bad fortnight without reading the whole table: a failing
 * extension writes one row per reason per day, so a small window can contain
 * nothing but noise. The last SUCCESS is fetched separately by both, because no
 * window is the right shape for "when did it last work".
 */
export const RECENT_RUNS = 120

const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
]

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Today, in the timezone the runs are dated in.
 *
 * NOT the browser's local date, and this is the whole point. Every `run_date`
 * in the table is computed in America/New_York — by `easternDate()` in the
 * extension and independently by the Pages Function, which takes the date from
 * itself and never from the caller. Comparing those against a device clock
 * means a laptop left on UTC reads "updated yesterday" about a run that
 * happened this morning, for the five hours between local midnight and
 * midnight in New York.
 *
 * Built from `formatToParts` rather than a locale string, because
 * `toLocaleDateString('en-CA')` producing `YYYY-MM-DD` is a convention rather
 * than a guarantee, and the one thing this must never do is return a date in
 * some other order.
 */
export function easternDate(now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now)
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
    const out = `${get('year')}-${get('month')}-${get('day')}`
    /**
     * Checked, not assumed, and this is not defensive padding.
     *
     * A build without full ICU data does not necessarily throw — it can return
     * a `parts` array with a piece missing, and `''` for a piece produces
     * `-08-18`. Nothing downstream refuses that: `weekdaysBetween` returns 0
     * for an unreadable date, so EVERY age check collapses to "no time has
     * passed" and a month-stale roster reads calm blue forever. That is the
     * reassuring lie the cutoff date exists to prevent, arriving through the
     * other input.
     */
    if (isDate(out)) return out
  } catch {
    // `Intl.DateTimeFormat` throws RangeError where the zone is unknown. This
    // runs during render, and there is no error boundary in this app — an
    // uncaught throw here would take the whole dashboard down to avoid saying
    // one sentence about a date.
  }
  return localFallbackDate(now)
}

/**
 * The device's own date, used only when the Eastern one cannot be had.
 *
 * Wrong by up to a day for five hours each evening, which is a great deal
 * better than wrong by a month or not rendering at all.
 */
function localFallbackDate(now: Date): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * `YYYY-MM-DD` to a local Date at midday.
 *
 * Midday, not midnight, for the same reason `daysBetween` uses it in `drips.ts`:
 * clocks move at 2am and nothing moves at noon, so no daylight-saving shift can
 * round a day the wrong way. And built from the parts rather than parsed —
 * `new Date('2026-08-18')` is midnight UTC, which is the 17th in New York.
 */
function at(date: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!m) return null
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3])
  const dt = new Date(y, mo - 1, d, 12)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
  return dt
}

/** Is this `YYYY-MM-DD` a real day on the calendar? */
export const isDate = (date: string): boolean => at(date) !== null

/** The day of the week, spelled out. English, not the machine's locale. */
export function dayName(date: string): string | null {
  const dt = at(date)
  return dt ? DAY_NAMES[dt.getDay()] : null
}

/**
 * A date a person would read aloud. "11 August", or "11 August 2025" if it is
 * not this year.
 *
 * Hand-rolled rather than `toLocaleDateString`, which is the machine's locale
 * and produced "18 August 2026" on an en-GB box where the copy beside it says
 * August 18 — and, worse, `2026年8月18日` on a Japanese one. Every other
 * sentence on this screen is English; so is this.
 */
export function spellDate(date: string, today: string): string {
  const dt = at(date)
  if (!dt) return date
  const sameYear = today.slice(0, 4) === date.slice(0, 4)
  const base = `${dt.getDate()} ${MONTH_NAMES[dt.getMonth()]}`
  return sameYear ? base : `${base} ${dt.getFullYear()}`
}

/**
 * The wall-clock reading of an instant, in the timezone the runs are dated in.
 *
 * `finished_at` is a `timestamptz` and arrives as UTC. Rendered with the
 * device's clock it says 10:53 on a laptop left on London time, about a run
 * that happened at 06:53 where Renee is — while the headline beside it is
 * already speaking in Eastern terms. Two clocks in one sentence is worse than
 * no clock at all.
 *
 * `hour12: false` rather than reading the `dayPeriod` part, because AM/PM is
 * itself a localised string and this file has already been burned once by
 * `toLocaleDateString` returning `2026年8月18日`. The 12-hour reading is
 * computed below, in English, from a number.
 */
function easternClock(iso: string): { date: string; hour: number; minute: number } | null {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(at)
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
    const date = `${get('year')}-${get('month')}-${get('day')}`
    const hour = Number(get('hour'))
    const minute = Number(get('minute'))
    /**
     * Midnight comes back as `24` from some ICU builds under `hour12: false` —
     * the h24 cycle rather than h23. Left alone the 12-hour arithmetic below
     * turns it into "12:07 PM": a run recorded seven minutes after midnight,
     * displayed as lunchtime.
     */
    const h = hour === 24 ? 0 : hour
    if (!isDate(date) || !Number.isInteger(h) || !Number.isInteger(minute)) return null
    if (h < 0 || h > 23 || minute < 0 || minute > 59) return null
    return { date, hour: h, minute }
  } catch {
    // Unknown zone on an ICU-less build. The caller prints no time rather than
    // the wrong one.
    return null
  }
}

/**
 * An instant, spelled out for somebody reading it rather than parsing it:
 * "Tuesday, 18 August at 6:53 AM".
 *
 * Null rather than a partial answer. Every caller treats this as an optional
 * extra on a sentence that is already complete and already true — so a missing
 * stamp costs a little precision, and a wrong one costs the whole line its
 * credibility.
 */
export function spellStamp(iso: string | null | undefined, today: string): string | null {
  if (!iso) return null
  const clock = easternClock(iso)
  if (!clock) return null
  const day = dayName(clock.date)
  if (!day) return null
  const suffix = clock.hour < 12 ? 'AM' : 'PM'
  // 0 and 12 both read as 12 — midnight is 12:00 AM, noon is 12:00 PM.
  const hour = clock.hour % 12 === 0 ? 12 : clock.hour % 12
  const minute = String(clock.minute).padStart(2, '0')
  return `${day}, ${spellDate(clock.date, today)} at ${hour}:${minute} ${suffix}`
}

/**
 * Days the roster is not expected to update, beyond weekends.
 *
 * WHY THIS LIST HAS TO EXIST
 *
 * The pull happens when a PERSON opens a laptop. Counting Monday to Friday
 * fixed the weekend and left every public holiday behind: last run the
 * Wednesday before Thanksgiving, and by Friday the count is two working days
 * and the alarm goes off at a brokerage where nobody is working. This file's
 * own header argues that "Renee learns within a fortnight that the warning
 * means nothing, and then it is worthless on the day it is real"; weekday
 * counting only solved half of it.
 *
 * ── WHY IT IS SHORT, AND WHY THAT MATTERS MORE THAN BEING COMPLETE ──────────
 *
 * The first version of this list was the full federal calendar: MLK,
 * Presidents' Day, Juneteenth, Columbus Day, Veterans Day. That was a worse
 * defect than the one it fixed, and in the more dangerous direction. **Nothing
 * in this project establishes that this brokerage closes on those days**, and a
 * residential brokerage generally does not — so on Columbus Day and the day
 * after, with a dead roster, the strip read a calm "The roster updated Friday."
 * Two open business days reported as fine. A list that is too long makes a
 * broken roster look healthy; a list that is too short only costs a false
 * alarm. Those are not the same mistake and they do not deserve the same
 * benefit of the doubt.
 *
 * So this is the short list — the days an office is actually shut: New Year,
 * Memorial Day, Independence Day, Labor Day, Thanksgiving and the Friday after
 * it, and Christmas. **It is still an assumption, and Renee has not confirmed
 * it.** If she works any of these, take it out.
 *
 * Observed dates, not nominal ones: 4 July 2026 is a Saturday, so the day off
 * is Friday the 3rd. Weekend dates are left out entirely — a holiday that falls
 * on a Sunday subtracts nothing from a count that never included it.
 *
 * These were generated from the usual rules and pasted here, rather than
 * computed at runtime: a date table you can read and correct beats an
 * eight-line algorithm nobody checks. The suite recomputes the whole-year
 * totals against them.
 *
 * **EVERY ENTRY MUST BE A WEEKDAY, AND THEY MUST BE UNIQUE AND SORTED.** The
 * subtraction loop below trusts all three rather than re-checking them; the
 * suite asserts them of the list itself, which is where the invariant lives.
 *
 * **IT RUNS OUT AFTER CHRISTMAS 2031, AND THAT IS DELIBERATE.** (New Year's
 * Day 2032 is the first uncovered closure — the list always ends a few days
 * short of a clean year boundary, which is harmless because the alarm below
 * fires a full year before anything is missed.)
 * `test-syncstatus.mjs` fails a year before the list stops covering today, so
 * it becomes somebody's problem loudly and early instead of quietly reverting
 * to false alarms nobody connects to a table written years earlier.
 */
export const HOLIDAY_LIST = [
  '2026-01-01', '2026-05-25', '2026-07-03', '2026-09-07', '2026-11-26', '2026-11-27',
  '2026-12-25', '2027-01-01', '2027-05-31', '2027-07-05', '2027-09-06', '2027-11-25',
  '2027-11-26', '2027-12-24', '2027-12-31', '2028-05-29', '2028-07-04', '2028-09-04',
  '2028-11-23', '2028-11-24', '2028-12-25', '2029-01-01', '2029-05-28', '2029-07-04',
  '2029-09-03', '2029-11-22', '2029-11-23', '2029-12-25', '2030-01-01', '2030-05-27',
  '2030-07-04', '2030-09-02', '2030-11-28', '2030-11-29', '2030-12-25', '2031-01-01',
  '2031-05-26', '2031-07-04', '2031-09-01', '2031-11-27', '2031-11-28', '2031-12-25',
] as const

export const HOLIDAYS: ReadonlySet<string> = new Set(HOLIDAY_LIST)

/** The last day the list can speak for. Asserted to BE the last entry. */
export const HOLIDAYS_THROUGH = '2031-12-25'

/** A day nobody is expected to be at a desk. */
export function isClosed(date: string): boolean {
  if (HOLIDAYS.has(date)) return true
  const d = at(date)
  if (!d) return false
  const day = d.getDay()
  return day === 0 || day === 6
}

/**
 * Working days from `from` to `to`, counting `to` and not counting `from`.
 *
 * So a run on Friday looked at on the following Monday is **1**, which is what
 * anybody counting on their fingers would say: one working day has gone by
 * without an update. Saturday and Sunday are not working days and do not count.
 *
 * Arithmetic rather than a loop, and that is not premature cleverness. The loop
 * version needed a guard against spinning the browser on a pathological pair of
 * dates, and a guard means a *capped* number — which was then rendered on
 * screen as fact, so ten years of silence and twenty-six years of silence
 * produced 286 and 285. A smaller number for a longer silence is worse than no
 * number.
 *
 * Negative spans and unreadable dates give 0 rather than a wrong number: a run
 * dated in the future is a clock problem, not a staleness problem, and
 * inventing a count for it would put an alarm on screen about nothing.
 */
export function weekdaysBetween(from: string, to: string): number {
  const a = at(from)
  const b = at(to)
  if (!a || !b) return 0
  const start = new Date(a.getTime())
  start.setDate(start.getDate() + 1)
  if (start > b) return 0

  const span = Math.round((b.getTime() - start.getTime()) / 86_400_000) + 1
  const whole = Math.floor(span / 7)
  let count = whole * 5
  const firstDay = start.getDay()
  for (let i = 0; i < span % 7; i++) {
    const day = (firstDay + i) % 7
    if (day !== 0 && day !== 6) count++
  }

  /**
   * Holidays are subtracted rather than skipped in a loop.
   *
   * The arithmetic above exists because the loop version needed a cap against
   * spinning on a pathological pair of dates, and a cap became a WRONG NUMBER on
   * screen. That reasoning still holds, so this walks the holiday list — sixty
   * entries, fixed — rather than the span, which can be decades.
   */
  /**
   * No clamp on the result, and that is not an oversight.
   *
   * Every entry is a unique weekday inside the span, so each one subtracts a
   * day the loop above actually counted — the total cannot go negative. A
   * `Math.max(0, …)` here would look prudent and be untestable, and an
   * untestable guard is how a broken invariant hides. The invariant is asserted
   * against the list instead, where it can fail loudly.
   */
  for (const day of HOLIDAYS) {
    const d = at(day)
    if (!d || d < start || d > b) continue
    count--
  }
  return count
}

/** Runs worth reasoning about: this source, and after the seeding cutoff. */
export const usableRuns = (runs: readonly SyncRun[], kind: string): SyncRun[] =>
  runs
    .filter((r) => r.kind === kind && isDate(r.run_date) && r.run_date >= IGNORE_BEFORE)
    // Newest first, and `id` breaks the tie so two runs on one date have a
    // definite order rather than whatever the read happened to return.
    .sort((a, b) => (a.run_date === b.run_date ? b.id - a.id : a.run_date < b.run_date ? 1 : -1))

/**
 * How long ago, in words she would use.
 *
 * "3 days ago" is arithmetic; "Friday" is a memory. Both are given when they
 * differ, because "Friday" alone stops being useful once it is a fortnight old
 * — and at that point the date is spelled out rather than printed as
 * `2026-08-11`, which is a machine's way of writing a date in a sentence
 * written for somebody who does not want to think about machines.
 */
export function howLongAgo(runDate: string, today: string): string {
  if (runDate === today) return 'this morning'
  const working = weekdaysBetween(runDate, today)
  const name = dayName(runDate)
  const a = at(runDate)
  const b = at(today)
  const calendar = a && b ? Math.round((b.getTime() - a.getTime()) / 86_400_000) : 0
  if (calendar === 1) return 'yesterday'
  const label = calendar >= 2 && calendar <= 6 && name ? name : spellDate(runDate, today)
  if (working <= 1) return label
  return `${label} — ${working} working days ago`
}

export type SyncTone = 'ok' | 'warn' | 'bad'

const WORSE: Record<SyncTone, number> = { ok: 0, warn: 1, bad: 2 }
/** The more alarming of two tones. Exported: the component has evidence the table does not. */
export const worst = (a: SyncTone, b: SyncTone): SyncTone => (WORSE[a] >= WORSE[b] ? a : b)

export type SyncStatus = {
  tone: SyncTone
  /** The sentence. Always present, always true. */
  headline: string
  /** What went wrong, if the most recent word we have is that something did. */
  detail: string | null
  /** The last run that actually succeeded, if there is one. */
  lastOk: SyncRun | null
  /**
   * When the last successful run finished, spelled out — "Tuesday, 18 August at
   * 6:53 AM", in Eastern, whatever the device's clock says.
   *
   * Null in exactly three cases: there has never been a successful run; the row
   * carries neither timestamp; or the timestamp it carries cannot be read as a
   * date. It is NOT suppressed for disagreeing with the headline — see the note
   * at the assignment for why that was tried and taken out again.
   *
   * The headline never depends on this. It is an addition to a sentence that
   * already stands on its own, which is what makes returning null safe.
   */
  stamp: string | null
}

/**
 * The strip.
 *
 * TWO DIFFERENT FAILURES, and the reason this is computed here rather than read
 * from a column:
 *
 *   - **Ran and broke.** The extension knows why and posts it, so there is a
 *     `failed` row with words in it.
 *   - **Never started.** Nothing reports anything, because nothing ran. There
 *     is no row at all.
 *
 * A column can only ever describe the first. The question that covers both is
 * "when did we last hear that it WORKED", which is answered by the absence of
 * rows as much as by their contents.
 *
 * A FAILURE COLOURS THE STRIP, and that was a real defect rather than a
 * refinement. Tone was computed from the last success alone, so "it succeeded
 * yesterday and broke this morning" — the single most common way this goes
 * wrong — rendered calm blue, unbolded, with the reason in grey beside a
 * headline reading "The roster updated yesterday." True, reassuring, and about
 * the wrong day.
 */
/**
 * A RUN CAN SUCCEED AND STILL BE WRONG, and until now nothing was looking.
 *
 * Both of these states close the day as `result: 'ok'`. The watchdog asks
 * whether a run arrived and is satisfied; the strip shows the last successful
 * run and is calm; `sync_runs.stats` holds the evidence and nothing reads it.
 * They are the shape of failure this project keeps finding and keeps having to
 * fix in a different place:
 *
 * - **A card stopped arriving.** The extension omits a card it could not fetch
 *   rather than faking it as empty — deliberately, so that a slow morning costs
 *   one card and not the whole day. But a card that fails EVERY morning is then
 *   a month of no first-closing congratulations behind three green surfaces.
 *
 * - **The baseline was never claimed.** `milestonesBaselined: false` on a
 *   successful run means `firstRun` is still true, so `plan` records every
 *   event and drafts none of them. Left alone it never resolves by itself, and
 *   the symptom is silence: every screen green, every run `ok`, and no email
 *   ever drafted again.
 *
 * `=== false`, not falsy. Runs written before that stat existed have no such
 * key at all, and absent is not the same as false — reading it loosely would
 * light up every historical row.
 *
 * Order matters: a missing card is usually the CAUSE of an unclaimed baseline
 * (`baselineReady` requires every card present), so name the cause rather than
 * the symptom when both are true.
 */
export type SyncHealth = { subject: string; detail: string } | null

export function healthOf(run: SyncRun | null | undefined): SyncHealth {
  if (!run || run.result !== 'ok') return null
  const stats = run.stats ?? {}

  /**
   * `activeRoster` DOES NOT COUNT, on purpose — Chris, 2026-09-09, after it
   * alarmed Renee two mornings running for no real reason.
   *
   * It is the heaviest card Metabase answers (~26s, `extension/background.js`'s
   * own comment on `PULL_CONCURRENCY`) and so the one most likely to time out
   * on a slow morning. Its only job is a next-day catch net for an agent the
   * New Joiners card missed (`sc-licensed-trigger-build-2026-09-02.md`) — and
   * that catch net reruns every single morning regardless, checking the WHOLE
   * roster against who is already known, not just whoever this run's card
   * happened to carry. So a miss here costs nothing today and repairs itself
   * tomorrow without anyone doing anything, which is exactly the kind of
   * thing this strip should stay quiet about.
   *
   * Still written to `stats.cardsMissing` untouched, in full, by
   * `sync-metabase.ts` — this filters what is SHOWN, not what is RECORDED, so
   * a card failing for real reasons every morning is still there to find.
   * Every other card here is still surfaced: `capped`, `elite`,
   * `firstClosings` and the rest genuinely can mean a real congratulation or
   * welcome silently not drafted today, which is worth knowing about today,
   * not tomorrow.
   */
  const missing = Array.isArray(stats.cardsMissing)
    ? stats.cardsMissing.filter(
        (c): c is string =>
          typeof c === 'string' && c.trim() !== '' && !c.startsWith('activeRoster:'),
      )
    : []
  if (missing.length) {
    return {
      subject: 'some Metabase cards stopped arriving',
      detail: `The last update ran, but not everything arrived from Metabase — ${missing.join('; ')}`,
    }
  }

  if (stats.milestonesBaselined === false) {
    return {
      subject: 'milestones are being recorded but never drafted',
      detail:
        'The last update ran, but it still has not recorded its starting point — so every ' +
        'birthday, cap and closing is being written down and none of them is being drafted. ' +
        'This usually means a Metabase card came back empty.',
    }
  }

  return null
}

/**
 * How many successful runs in a row are unhealthy, most recent first.
 *
 * Counted in RUNS rather than working days, and the two are not the same thing
 * — say so rather than implying otherwise. In practice there is one successful
 * run per weekday, so they track closely, and it is the closeness that lets
 * `shouldAlert` be reused: one bad morning is a bad morning, and nobody should
 * be emailed about it. Two in a row is a pattern.
 *
 * Rows that are not `ok` are stepped OVER, not treated as the end of the run.
 * Three laptops sync each morning and the losers write `failed` rows on days
 * that went perfectly — the same trap `newerThanSuccess` exists for.
 */
function unhealthyStreak(usable: readonly SyncRun[]): number {
  let n = 0
  for (const run of usable) {
    if (run.result !== 'ok') continue
    if (!healthOf(run)) break
    n += 1
  }
  return n
}

export function syncStatus(
  runs: readonly SyncRun[],
  kind: string,
  today: string,
  label: string,
): SyncStatus {
  const usable = usableRuns(runs, kind)
  const lastOk = usable.find((r) => r.result === 'ok') ?? null
  const latest = usable[0] ?? null
  /**
   * Strictly a LATER DAY, not a later row.
   *
   * An `id` tiebreak looked right and was the opposite of right. Three laptops
   * run the same morning and each one's alarm is guarded only by its own
   * `lastSuccessDate`, so machine A succeeding at 07:15 does not stop machine
   * B trying at 07:20 — and B, signed out, writes a `failed` row for the same
   * day with a higher id. Failed rows sit outside the one-per-day index on
   * purpose, so this always succeeds. The strip then went amber and bold every
   * single morning, with an action item, on a day the roster updated perfectly
   * — until somebody signed that one laptop back in. That is exactly the
   * "she learns it means nothing" outcome the weekday counting exists to avoid.
   *
   * If the day has a success, the day is covered. A failure after it is history.
   */
  const newerThanSuccess = latest && (!lastOk || latest.run_date > lastOk.run_date)

  /**
   * A failure is only worth reporting if it is the most recent word we have.
   * A run that broke on Tuesday and succeeded on Wednesday is history, and
   * putting it on screen would have her chasing something already fixed.
   */
  let detail: string | null = null
  let failureTone: SyncTone = 'ok'
  if (latest && newerThanSuccess) {
    if (latest.result === 'failed') {
      detail = latest.message || 'The last update did not finish, and no reason was recorded.'
      failureTone = 'warn'
    } else if (latest.result === 'aborted' && latest.reason === 'dry-run') {
      /**
       * A dry run answers `ok` to the extension, which then records the day as
       * done and stops retrying — while nothing was saved. Left switched on,
       * that is a roster that quietly stops updating and a strip that goes red
       * days later with no explanation, when the one word that explains it
       * ('dry-run') was sitting in the row the whole time.
       */
      /**
       * NOT "in the extension options". There is no such option — `options.html`
       * holds the endpoint, the state, the hour and the key, and nothing else.
       *
       * "A setting on the site" was the first correction and was no better: it
       * is a Cloudflare Pages environment variable, so a reader told to look on
       * the site finds nothing there either. Name the place, and say what "off"
       * means — `sync-metabase.ts` tests `=== '1'`, and Pages does not pick up
       * a variable change until the next deployment.
       */
      detail =
        'The last update was a practice run, so nothing was saved. Practice mode is SYNC_DRY_RUN in Cloudflare — remove it there, or set it to anything other than 1, and redeploy.'
      failureTone = 'warn'
    } else if (latest.result === 'running' && latest.run_date < today) {
      /**
       * A row left `running` is a third thing, and it reads differently to
       * anybody trying to work out what happened: an update STARTED and never
       * came back, rather than never starting at all.
       */
      detail = `An update started on ${spellDate(latest.run_date, today)} and never finished.`
      failureTone = 'warn'
    }
  }

  /**
   * A SUCCESSFUL RUN THAT IS QUIETLY WRONG STILL COLOURS THE STRIP.
   *
   * Only when there is no fresher failure to report: a run that broke outranks
   * a run that worked badly, and two sentences on one line is how the strip
   * came to say contradictory things once already. In practice they cannot
   * both be set — `detail` above is only written when the latest run is NOT the
   * success, and this only reads a run that is.
   */
  /**
   * `lastOk`, NOT `latest`. Two rows can share a date — three laptops sync each
   * morning and the losers write `failed` rows on days that went perfectly — so
   * the newest ROW is regularly not the newest RUN. Reading `latest` here made
   * a day report neither its failure (same date, so `newerThanSuccess` is
   * false) nor its health problem (not an `ok` row, so `healthOf` declines).
   * Caught by the "another laptop failing does not break the streak" test.
   */
  const health = healthOf(lastOk)
  if (health && !detail) {
    detail = health.detail
    failureTone = worst(failureTone, 'warn')
  }

  if (!lastOk) {
    /**
     * Day one is not a fault. Before the very first run there is nothing in the
     * table, and opening the app to a red "has never updated successfully" on
     * the morning the feature ships is alarming about nothing.
     */
    const waiting = weekdaysBetween(IGNORE_BEFORE, today) <= 1
    return {
      tone: waiting ? worst('warn', failureTone) : 'bad',
      headline: waiting
        ? detail
          ? // "the first update runs in the morning" asserts nothing has run.
            // Beside a detail saying something ran and broke, that is two
            // contradictory sentences on one line.
            `${label} has not updated yet — the first update tried and did not finish.`
          : `${label} has not updated yet — the first update runs in the morning.`
        : `${label} has never updated successfully.`,
      detail,
      lastOk: null,
      stamp: null,
    }
  }

  const working = weekdaysBetween(lastOk.run_date, today)
  const when = howLongAgo(lastOk.run_date, today)

  /**
   * One working day is NORMAL, not a warning.
   *
   * The run happens in the morning. Look at this at 8am before it has run and
   * the last success is yesterday — every single day. An amber strip every
   * morning until the alarm fires is the Monday problem wearing a different hat.
   */
  const ageTone: SyncTone = working <= 1 ? 'ok' : working === 2 ? 'warn' : 'bad'
  const tone = worst(ageTone, failureTone)

  return {
    tone,
    headline:
      ageTone === 'ok'
        ? `${label} updated ${when}.`
        : `${label} has not updated since ${when}.`,
    detail,
    lastOk,
    /**
     * `finished_at` first, falling back to `started_at`.
     *
     * The question the line answers is "when was the roster last brought up to
     * date", and that is the moment the run finished. `started_at` is there
     * because a row written by an older build may carry only that, and a time
     * a few seconds early is a far smaller lie than no time at all.
     *
     * `||` rather than `??`, so ANY falsy value falls back rather than only
     * null and undefined. The column is a `timestamptz` and Postgres cannot
     * store an empty string in one, so this is not repairing a state the schema
     * allows — it is refusing to depend on that being true. `??` was not wrong;
     * `||` is merely harder to make wrong later.
     *
     * ── A SUPPRESSION THAT WAS BUILT AND THEN REMOVED ────────────────────────
     *
     * The stamp and the headline can name different days: a run dated the 18th
     * that finished at 00:01 on the 19th renders "The roster updated
     * yesterday." beside "Wednesday, 19 August at 12:01 AM". A guard was added
     * to hide the stamp whenever the two disagreed, and it was a worse idea
     * than the problem.
     *
     * The stamp is the RELIABLE half. `run_date` is written by the extension's
     * `easternDate()`, which falls back to the device clock when it cannot
     * reach ICU; `finished_at` is a database timestamp read in Eastern. When
     * they disagree, the one to keep is the one that was not computed on a
     * laptop of unknown configuration — and the disagreement is itself the
     * only visible symptom of a machine with a wrong clock. Suppressing it
     * deleted the accurate sentence, kept the inaccurate one, and hid the
     * evidence in exactly the case worth noticing.
     */
    stamp: spellStamp(lastOk.finished_at || lastOk.started_at, today),
  }
}

/**
 * ── THE WATCHDOG ───────────────────────────────────────────────────────────
 *
 * The strip can only speak to somebody who is looking at it. The failure it
 * cannot cover is the one where nobody opens the dashboard at all — or where
 * Renee's laptop never wakes up, so the morning pull never runs, so no row is
 * written, so there is nothing for any screen to notice. That is invisible from
 * inside the app by construction.
 *
 * So something outside has to ask. This is what it asks, and it deliberately
 * lives in THIS file rather than in the job that runs it: the email and the
 * screen now say the same thing because they are the same function. Two
 * implementations of "is the roster stale" would eventually disagree, and the
 * one that was wrong would be the one nobody was looking at.
 */

/** Whether to email today, and what to say. */
export type Watchdog = {
  alert: boolean
  /** Working days since the last successful run. */
  working: number
  /** The subject line, if there is one to send. */
  subject: string
  /** The body, in the same words the strip uses. */
  body: string
}

/**
 * Whether today is a day to speak, given how many working days it has been.
 *
 * The requirement is "one alert, then silence until it recovers". Written as
 * `working >= 2` that needs somewhere to remember whether today's mail has
 * already gone, which means a table, which means a second thing to get out of
 * step. This is the same behaviour with nothing remembered.
 *
 * ── WHY EACH WINDOW IS TWO DAYS WIDE, NOT ONE ───────────────────────────────
 *
 * The first version alerted when the count was exactly 2, or exactly 5. That is
 * the same behaviour ONLY IF the check runs every single weekday — and it has no
 * way of knowing it didn't. Cron triggers are best effort: a deploy, a paused
 * Worker, a platform incident or the endpoint being down for an hour all drop a
 * day. Miss the day the count was 2 and it goes 1 → 3, and the first alert is
 * never sent at all. Miss both windows and a real outage is completely silent,
 * which is total failure of the only thing this exists to do.
 *
 * A two-day window survives one dropped day at each point and still sends at
 * most two emails per window — given the guard in `watchdogVerdict` that
 * refuses to speak on a closed day. Without that guard a holiday stalls the
 * count inside a window and the same mail goes out on every day of it; the two
 * belong together.
 *
 * ── AND WHY IT COMES BACK EVERY WEEK ────────────────────────────────────────
 *
 * With only 2-3 and 5-6, anything past six working days was silent forever. Two
 * real cases land there and both are the worst case rather than an edge: a
 * system that has NEVER run successfully (the count starts at the cutoff and
 * only grows), and an outage that has already been going a fortnight. Both used
 * to produce nothing at all.
 *
 * So after ten working days it speaks again roughly weekly — 10-11, 15-16,
 * 20-21 — which is frequent enough not to be forgotten and rare enough not to
 * become wallpaper. Recovery still resets everything, because the count returns
 * to zero on its own.
 */
export function shouldAlert(working: number): boolean {
  if (!Number.isFinite(working) || working < 2) return false
  if (working <= 3) return true
  if (working === 5 || working === 6) return true
  if (working >= 10) return working % 5 <= 1
  return false
}

/**
 * How to bring `daily_metabase` back to life, in the watchdog email.
 *
 * Pulled out to a named default rather than left inline, now that a second
 * kind (`scheduled_sends`, 2026-08-21) shares this function but not this
 * advice — a Cloudflare Cron Trigger has no laptop and no Chrome extension
 * for anybody to go open. `watchdog.ts` passes its own text for that kind;
 * every caller that does not is still told about the laptop, unchanged.
 */
const METABASE_RECOVERY =
  'The roster updates when Chrome is open on the laptop with the Agent Care ' +
  'extension installed. Opening that laptop and leaving Chrome running for a ' +
  'few minutes is usually all this needs.\n\n' +
  'If it is already open, sign in to Metabase in that browser and press ' +
  '"Update now" at the top of the roster.'

export function watchdogVerdict(
  runs: readonly SyncRun[],
  kind: string,
  today: string,
  label: string,
  /**
   * What to tell somebody to go do about it. Optional and defaulted, so
   * every existing call site — the strip, any test that predates this
   * parameter — keeps meaning exactly what it meant before. Only a caller
   * checking a kind with different recovery steps needs to pass one.
   */
  recovery: string = METABASE_RECOVERY,
): Watchdog {
  const status = syncStatus(runs, kind, today, label)
  /**
   * With no successful run ever, the count starts at the cutoff rather than at
   * nothing — otherwise a system that has never worked is indistinguishable
   * from one that worked this morning.
   *
   * That count then grows without bound, which was a real defect while the rule
   * was an equality: it passed 2 and 5 once, in the first week of the cutoff
   * date, and after that a wiped table or a renamed `kind` was silent forever.
   * The weekly clause in `shouldAlert` is what makes an unbounded count keep
   * speaking instead of running away from the only days it was listened for.
   */
  const from = status.lastOk ? status.lastOk.run_date : IGNORE_BEFORE
  const working = weekdaysBetween(from, today)

  /**
   * NOTHING IS SENT ON A DAY NOBODY IS WORKING, and this guard is the other
   * half of the holiday list rather than a nicety.
   *
   * Holidays shrink the count, so on a holiday the count STALLS — and the cron
   * knows nothing about holidays and fires anyway. Without this, a stall inside
   * an alert window re-sent the same email every day of it: measured at four
   * identical "6 working days" emails across Thanksgiving week 2026, two of
   * them landing on Thanksgiving Day and the Friday after — the exact two
   * mornings the holiday list was added to protect.
   *
   * With it, every window sends at most two, and never on a day off.
   */
  if (isClosed(today)) return { alert: false, working, subject: '', body: '' }

  /**
   * ARRIVING IS NOT THE SAME AS WORKING, and this endpoint only ever asked the
   * first question.
   *
   * Checked BEFORE the staleness test returns nothing, because the whole point
   * of these two states is that the run is arriving perfectly — `working` is 0
   * and always will be, so the staleness path can never speak for them.
   * Staleness still wins when both apply: a roster that stopped updating is
   * worse than one updating badly.
   *
   * `shouldAlert` is reused so this inherits the same cadence rather than
   * inventing a second one — silent on the first bad morning, then the same
   * back-off. An alert nobody can act on twice is an alert she stops reading,
   * which is the failure the whole weekday-counting exercise was written to
   * avoid.
   */
  if (!shouldAlert(working)) {
    const usable = usableRuns(runs, kind)
    // The last SUCCESSFUL run, for the same reason as above — the newest row is
    // regularly another laptop's failure on a morning that went perfectly.
    const health = healthOf(usable.find((r) => r.result === 'ok'))
    const streak = unhealthyStreak(usable)
    if (health && shouldAlert(streak)) {
      return {
        alert: true,
        working,
        subject: `${label} — ${health.subject}`,
        body:
          `${health.detail}\n\n` +
          `The update itself is running and finishing normally, so nothing on the ` +
          `dashboard will look wrong. This has been the case for ${streak} runs in a row.\n\n` +
          `This one needs somebody to look at the Metabase dashboard rather than ` +
          `at the laptop.`,
      }
    }
    return { alert: false, working, subject: '', body: '' }
  }

  const stamp = status.stamp ? `\n\nThe last successful update finished ${status.stamp}.` : ''
  const reason = status.detail ? `\n\nWhat it said last: ${status.detail}` : ''
  return {
    alert: true,
    working,
    subject: `${label} has not updated for ${working} working days`,
    /**
     * The body opens with the strip's own sentence, so somebody who reads the
     * email and then opens the dashboard is not told two different things.
     */
    body: `${status.headline}${stamp}${reason}\n\n${recovery}`,
  }
}

/**
 * ── WEEKLY CADENCE (reZEN) ──────────────────────────────────────────────
 *
 * `syncStatus`/`watchdogVerdict` above are built for a job that runs every
 * WEEKDAY — that is the whole reason `weekdaysBetween` and the holiday list
 * exist, and reusing them for a job that runs once a WEEK would be exactly
 * the "counts a normal gap as a problem" bug their own header warns about,
 * just at the other cadence: a reZEN sync that last succeeded four calendar
 * days ago is completely normal (it only runs on Mondays), but
 * `weekdaysBetween` would already call that "3 working days" and light the
 * strip amber every single Thursday and Friday.
 *
 * So this is a parallel, smaller set of functions — calendar days, not
 * working days, and no holiday list, because the reZEN pull is not tied to
 * a business day the way the Metabase pull is. Added 2026-08-22 alongside
 * `sync-rezen.ts`, per `claude/rezen-weekly-sync-design.md` §6 (previously
 * flagged there as PROPOSED and unbuilt).
 *
 * Thresholds are PROPOSED, not measured against a real weekly cadence yet
 * (there is no run history to measure against on day one) — cheap to
 * retune once real data exists: normal is up to a week late (the run
 * happens Monday morning; reading this on Sunday is seven days after last
 * Monday and that is not a problem), amber from over a week to just under
 * two, red at two missed cycles or more.
 */

/**
 * Calendar days between two `YYYY-MM-DD` dates. Same local-noon arithmetic
 * `weekdaysBetween` uses (`at()`, above) so the two never disagree about
 * what day it is over a DST boundary. Not clamped at zero, for the same
 * reason `weekdaysBetween` isn't: a caller passing dates the wrong way
 * round is a bug worth seeing, not a negative number worth hiding.
 */
export function daysBetween(from: string, to: string): number {
  const a = at(from)
  const b = at(to)
  if (!a || !b) return 0
  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** Up to a week late is normal for a job that runs once a week. */
export function weeklyAgeTone(days: number): SyncTone {
  if (days <= 7) return 'ok'
  if (days <= 13) return 'warn'
  return 'bad'
}

/**
 * Same "alert, then go quiet, then come back weekly" shape as `shouldAlert`,
 * rebuilt on calendar days: silent through the normal week, a window that
 * opens once the run is a few days later than its Monday slot, then silence
 * again until it has clearly been dropped rather than just late, at which
 * point it resumes roughly weekly so a long outage cannot go permanently
 * silent.
 */
export function weeklyShouldAlert(days: number): boolean {
  if (!Number.isFinite(days) || days < 10) return false
  if (days <= 11) return true
  if (days >= 14) return days % 7 <= 1
  return false
}

export type WeeklyStatus = {
  tone: SyncTone
  headline: string
  detail: string | null
  lastOk: SyncRun | null
  stamp: string | null
}

/**
 * "Weeks ago" / "days ago" phrasing instead of `howLongAgo`'s "working days
 * ago" — a weekly job that is 10 days overdue is not usefully described in
 * working days, and saying so would be borrowing the daily job's vocabulary
 * for a cadence it doesn't fit.
 */
function howLongAgoWeekly(runDate: string, today: string): string {
  if (runDate === today) return 'this morning'
  const days = daysBetween(runDate, today)
  if (days === 1) return 'yesterday'
  const name = dayName(runDate)
  if (days >= 2 && days <= 6 && name) return name
  if (days === 7) return 'a week ago'
  if (days > 7 && days < 14) return `${spellDate(runDate, today)} — ${days} days ago`
  const weeks = Math.round(days / 7)
  return `${spellDate(runDate, today)} — ${weeks} week${weeks === 1 ? '' : 's'} ago`
}

/**
 * `syncStatus`'s counterpart for a weekly source. Deliberately does not
 * call `healthOf` — that reads `stats.cardsMissing`/`milestonesBaselined`,
 * which are Metabase-card concepts with no reZEN equivalent yet. A quietly-
 * wrong-but-successful reZEN run (e.g. far fewer agents than the 825
 * baseline) is `sync-rezen.ts`'s own job to refuse outright — see its
 * proportional ceiling — rather than something this display reasons about
 * after the fact.
 */
export function weeklySyncStatus(
  runs: readonly SyncRun[],
  kind: string,
  today: string,
  label: string,
): WeeklyStatus {
  const usable = usableRuns(runs, kind)
  const lastOk = usable.find((r) => r.result === 'ok') ?? null
  const latest = usable[0] ?? null
  const newerThanSuccess = latest && (!lastOk || latest.run_date > lastOk.run_date)

  let detail: string | null = null
  let failureTone: SyncTone = 'ok'
  if (latest && newerThanSuccess) {
    if (latest.result === 'failed') {
      detail = latest.message || 'The last update did not finish, and no reason was recorded.'
      failureTone = 'warn'
    } else if (latest.result === 'running' && latest.run_date < today) {
      detail = `An update started on ${spellDate(latest.run_date, today)} and never finished.`
      failureTone = 'warn'
    }
  }

  if (!lastOk) {
    // Day one is not a fault, same reasoning as `syncStatus` — a fresh
    // cutoff with nothing run yet is expected, not evidence of anything.
    const waiting = daysBetween(IGNORE_BEFORE, today) <= 7
    return {
      tone: waiting ? worst('warn', failureTone) : 'bad',
      headline: waiting
        ? detail
          ? `${label} has not updated yet — the first update tried and did not finish.`
          : `${label} has not updated yet — the first weekly update has not run yet.`
        : `${label} has never updated successfully.`,
      detail,
      lastOk: null,
      stamp: null,
    }
  }

  const days = daysBetween(lastOk.run_date, today)
  const when = howLongAgoWeekly(lastOk.run_date, today)
  const ageTone = weeklyAgeTone(days)
  const tone = worst(ageTone, failureTone)

  return {
    tone,
    headline: ageTone === 'ok' ? `${label} updated ${when}.` : `${label} has not updated since ${when}.`,
    detail,
    lastOk,
    stamp: spellStamp(lastOk.finished_at || lastOk.started_at, today),
  }
}

/**
 * `watchdogVerdict`'s counterpart for a weekly source. Same shape and same
 * caller contract (`SourceResult.working` in `watchdog.ts` just becomes
 * calendar days rather than working days for a source that uses this path
 * — documented at the call site, not renamed here, so the JSON shape
 * external callers read stays uniform across every `kind`).
 */
export function weeklyWatchdogVerdict(
  runs: readonly SyncRun[],
  kind: string,
  today: string,
  label: string,
  recovery: string,
): Watchdog {
  const status = weeklySyncStatus(runs, kind, today, label)
  const from = status.lastOk ? status.lastOk.run_date : IGNORE_BEFORE
  const days = daysBetween(from, today)

  if (!weeklyShouldAlert(days)) return { alert: false, working: days, subject: '', body: '' }

  const stamp = status.stamp ? `\n\nThe last successful update finished ${status.stamp}.` : ''
  const reason = status.detail ? `\n\nWhat it said last: ${status.detail}` : ''
  return {
    alert: true,
    working: days,
    subject: `${label} has not updated in ${days} days`,
    body: `${status.headline}${stamp}${reason}\n\n${recovery}`,
  }
}
